import { supabase } from "@/integrations/supabase/client";

export const PERSON_PHOTO_URL_PREFIX = "person-photo://";

const PERSON_PHOTOS_BUCKET = "person-photos";
const SIGNED_OBJECT_PATH = `/storage/v1/object/sign/${PERSON_PHOTOS_BUCKET}/`;

export const PERSON_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

export function createPersonPhotoReference(filePath: string): string {
  return `${PERSON_PHOTO_URL_PREFIX}${filePath}`;
}

/**
 * Validate a candidate person-photo file. Returns an error message to show the
 * user, or null when the file is acceptable. Shared by the admin person form
 * and member self-service so both enforce the same image/size limits.
 */
export function validatePersonPhotoFile(file: File): string | null {
  if (!file.type.startsWith("image/")) {
    return "Please upload an image file";
  }
  if (file.size > PERSON_PHOTO_MAX_BYTES) {
    return "Image must be less than 2MB";
  }
  return null;
}

/**
 * Upload a person photo to the organization-scoped storage bucket and return a
 * stable `person-photo://` reference to persist on the person record. The
 * reference is resolved to a signed URL on render by {@link resolvePersonPhotoUrl}.
 */
export async function uploadPersonPhoto(file: File, organizationId: string): Promise<string> {
  if (!organizationId) {
    throw new Error("No organization selected");
  }
  const fileExt = file.name.split(".").pop() || "jpg";
  const filePath = `${organizationId}/people/${crypto.randomUUID()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(PERSON_PHOTOS_BUCKET)
    .upload(filePath, file, { upsert: true });

  if (uploadError) throw uploadError;

  return createPersonPhotoReference(filePath);
}

/** Pause between attempts to remove a photo; exported for tests. */
export const PERSON_PHOTO_REMOVE_RETRY_MS = [300, 1000];

/**
 * Remove a stored person photo from the bucket, retrying a transient failure. Resolves true when
 * the photo is gone (or there was none to remove) and false when it's still there, so the caller
 * can say so rather than report a clean result.
 */
export async function removePersonPhoto(photoUrl: string | null): Promise<boolean> {
  const path = photoUrl ? extractPersonPhotoPath(photoUrl) : null;
  if (!path) return true;
  for (let attempt = 0; ; attempt += 1) {
    let error: unknown = null;
    try {
      ({ error } = await supabase.storage.from(PERSON_PHOTOS_BUCKET).remove([path]));
    } catch (thrown) {
      // A transport failure (network, aborted request) is a failed attempt, not a rejection the
      // caller has to handle: the person is already gone, and only the photo is left.
      error = thrown;
    }
    if (!error) return true;
    if (attempt >= PERSON_PHOTO_REMOVE_RETRY_MS.length) {
      console.error("Failed to remove person photo:", error);
      return false;
    }
    await new Promise((resolve) => setTimeout(resolve, PERSON_PHOTO_REMOVE_RETRY_MS[attempt]));
  }
}

export function extractPersonPhotoPath(photoUrl: string): string | null {
  if (!photoUrl) {
    return null;
  }

  if (photoUrl.startsWith(PERSON_PHOTO_URL_PREFIX)) {
    const value = photoUrl.slice(PERSON_PHOTO_URL_PREFIX.length);
    return value || null;
  }

  try {
    const parsed = new URL(photoUrl);
    const markerIndex = parsed.pathname.indexOf(SIGNED_OBJECT_PATH);
    if (markerIndex === -1) {
      return null;
    }

    const encodedPath = parsed.pathname.slice(markerIndex + SIGNED_OBJECT_PATH.length);
    return encodedPath ? decodeURIComponent(encodedPath) : null;
  } catch {
    return null;
  }
}

const SIGNED_URL_TTL_SECONDS = 60 * 60;
/** Reuse a signed URL for most of its life, leaving a margin before it expires. */
const SIGNED_URL_REUSE_MS = 50 * 60 * 1000;

/**
 * One signed URL per stored photo, shared by every avatar and link that shows it: a profile
 * renders the same photo in several places (hero, details, "View photo"), and each used to
 * request its own URL. In-flight requests are shared too. Failures aren't cached. The cache is
 * cleared on sign-in and sign-out, so a URL signed for one session is never handed to the next.
 */
const signedUrlCache = new Map<string, { url: Promise<string>; expiresAt: number }>();
supabase.auth?.onAuthStateChange?.((event) => {
  if (event === "SIGNED_IN" || event === "SIGNED_OUT") signedUrlCache.clear();
});

/** Test hook: forget every cached signed URL. */
export function clearPersonPhotoUrlCache() {
  signedUrlCache.clear();
}

export async function resolvePersonPhotoUrl(photoUrl: string): Promise<string> {
  const personPhotoPath = extractPersonPhotoPath(photoUrl);
  if (!personPhotoPath) {
    return photoUrl;
  }

  const cached = signedUrlCache.get(personPhotoPath);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.url;
  }

  const url = (async () => {
    try {
      const { data, error } = await supabase.storage
        .from(PERSON_PHOTOS_BUCKET)
        .createSignedUrl(personPhotoPath, SIGNED_URL_TTL_SECONDS);
      if (error || !data?.signedUrl) {
        signedUrlCache.delete(personPhotoPath);
        return photoUrl;
      }
      return data.signedUrl;
    } catch (err) {
      // A thrown transport failure is evicted too, so the next lookup retries rather than
      // replaying the rejection until the entry expires.
      signedUrlCache.delete(personPhotoPath);
      throw err;
    }
  })();
  signedUrlCache.set(personPhotoPath, { url, expiresAt: Date.now() + SIGNED_URL_REUSE_MS });
  return url;
}
